import { useEffect, useState } from "react";
import useApi from "./useApi";

/**
 * One purchasable membership term, priced by Stripe.
 *
 * Every money field comes from the Stripe Price rather than from configuration, so the picker
 * cannot disagree with what the patient is actually charged. The single-plan version of this page
 * rendered a configured fallback price while checkout used whatever the Price said, and nothing
 * compared the two.
 */
export interface SubscriptionPlan {
  id: string;
  label: string;
  badge?: string | null;
  months: number;
  price: number;
  currency: string;
  /** Null for a one-time price — the term is bought outright rather than renewing. */
  interval?: string | null;
  intervalCount?: number | null;
  recurring: boolean;
  pricePerMonth: number;
}

export default function useSubscriptionPlans() {
  const api = useApi();
  const [plans, setPlans] = useState<SubscriptionPlan[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .get<SubscriptionPlan[]>("/subscription/plans")
      .then((res) => {
        if (!cancelled) setPlans(res.data ?? []);
      })
      .catch(() => {
        // Distinguished from "no plans configured" on purpose: an empty list is a deployment that
        // sells nothing, a failure is one whose pricing we could not read. The page says different
        // things for each, because "no plans available" over a network blip reads as the product
        // being withdrawn.
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { plans, failed };
}
